/**
 * Toggle Manager
 * 
 * Manages visibility of toggleable mesh groups/elements.
 * Supports prefix-based mesh naming (toggle_*) and grouped visibility.
 */

import * as THREE from 'three';
import type { IManager, ToggleElement } from '../types';

export interface ToggleState {
  elementId: string;
  visible: boolean;
}

export class ToggleManager implements IManager {
  private scene: THREE.Scene | null = null;
  private elements: ToggleElement[] = [];
  private state = new Map<string, boolean>(); // elementId -> visible
  private meshGroups = new Map<string, THREE.Object3D[]>(); // elementId -> meshes
  
  private onChangeCallback?: (elementId: string, visible: boolean) => void;
  
  /**
   * Initialize with scene and toggle element configuration
   */
  public async init(scene: THREE.Scene, config: { elements: ToggleElement[] }): Promise<void> {
    this.scene = scene;
    this.elements = config.elements;
    
    // Build mesh groups for each toggle element
    this.buildMeshGroups();
    
    // Apply default visibility states
    for (const element of this.elements) {
      this.setVisibility(element.id, element.defaultVisible, false);
    }
  }
  
  /**
   * Build groups of meshes for each toggle element
   */
  private buildMeshGroups(): void {
    if (!this.scene) return;
    
    this.meshGroups.clear();
    
    this.scene.traverse((object) => {
      if (object.name) {
        for (const element of this.elements) {
          if (this.meshMatchesElement(object.name, element.meshNames)) {
            if (!this.meshGroups.has(element.id)) {
              this.meshGroups.set(element.id, []);
            }
            this.meshGroups.get(element.id)!.push(object);
          }
        }
      }
    });
  }
  
  /**
   * Check if a mesh name matches any of the element's patterns
   */
  private meshMatchesElement(meshName: string, patterns: string[]): boolean {
    for (const pattern of patterns) {
      if (pattern.includes('*')) {
        // Wildcard matching (e.g., toggle_cable_*)
        const regex = new RegExp('^' + pattern.replace(/\*/g, '.*') + '$');
        if (regex.test(meshName)) return true;
      } else {
        // Exact match
        if (meshName === pattern) return true;
        // Check if mesh is child of named parent
        if (meshName.startsWith(pattern + '_')) return true;
      }
    }
    return false;
  }
  
  /**
   * Set visibility of a toggle element
   */
  public setVisibility(elementId: string, visible: boolean, notify = true): void {
    const meshes = this.meshGroups.get(elementId);
    if (!meshes) {
      console.warn(`Toggle element not found: ${elementId}`);
      return;
    }
    
    // Update visibility for all meshes in the group
    for (const mesh of meshes) {
      mesh.visible = visible;
    }
    
    // Update state
    this.state.set(elementId, visible);
    
    if (notify) {
      this.onChangeCallback?.(elementId, visible);
    }
  }
  
  /**
   * Toggle visibility of an element
   */
  public toggle(elementId: string): boolean {
    const current = this.state.get(elementId) ?? true;
    const newState = !current;
    this.setVisibility(elementId, newState);
    return newState;
  }
  
  /**
   * Get all toggle elements
   */
  public getElements(): ToggleElement[] {
    return this.elements;
  }
  
  /**
   * Get current state
   */
  public getState(): ToggleState[] {
    return Array.from(this.state.entries()).map(([elementId, visible]) => ({
      elementId,
      visible,
    }));
  }
  
  /**
   * Get visibility of a specific element
   */
  public isVisible(elementId: string): boolean {
    return this.state.get(elementId) ?? true;
  }
  
  /**
   * Set change callback
   */
  public onChange(callback: (elementId: string, visible: boolean) => void): void {
    this.onChangeCallback = callback;
  }
  
  /**
   * Show all elements
   */
  public showAll(): void {
    for (const element of this.elements) {
      this.setVisibility(element.id, true);
    }
  }
  
  /**
   * Hide all elements
   */
  public hideAll(): void {
    for (const element of this.elements) {
      this.setVisibility(element.id, false);
    }
  }
  
  /**
   * Reset to default visibility states
   */
  public resetToDefaults(): void {
    for (const element of this.elements) {
      this.setVisibility(element.id, element.defaultVisible);
    }
  }
  
  /**
   * Update (called each frame) - no-op for toggles
   */
  public update(_deltaTime: number): void {
    // Toggles don't need per-frame updates
  }
  
  /**
   * Clean up resources
   */
  public dispose(): void {
    // Restore all to visible before disposing
    for (const meshes of this.meshGroups.values()) {
      for (const mesh of meshes) {
        mesh.visible = true;
      }
    }
    
    this.meshGroups.clear();
    this.state.clear();
    this.elements = [];
    this.scene = null;
  }
}
